// @flow

import React, { type Node } from 'react';
import { getAmountUsd, getMerchant, useIncomingPayment } from '@tectonics/prs/incoming-payment';
import { getName as getMerchantName, getPublicIntegrationKey } from '@tectonics/prs/merchant';
import { getCountry } from '@tectonics/prs/payment-spec';
import { LANGUAGE_BY_COUNTRY, LANGUAGE_ENGLISH } from '@tectonics/translate/enums/language-by-country.enum';
import GooglePayButton from '@google-pay/button-react';
import { useUserInputValue } from '@tectonics/prs/user-input-values';


export function GoogleButton(): Node {
  const [, setGooglePayOutgoingPayment] = useUserInputValue('googlePayOutgoingPayment');
  const incomingPayment = useIncomingPayment();
  const merchant = getMerchant(incomingPayment);

  const currentLocale = LANGUAGE_BY_COUNTRY[getCountry(incomingPayment)] || LANGUAGE_ENGLISH;

  const environment = ENV_CONSTANTS.ENV === 'production'
    ? 'PRODUCTION'
    : 'TEST';

  const paymentRequest = buildGooglePayPaymentRequest({
    merchantPublicKey: getPublicIntegrationKey(merchant),
    merchantName: getMerchantName(merchant),
    amountUsd: getAmountUsd(incomingPayment).toString(),
  });

  return (
    <GooglePayButton
      environment={ environment }
      buttonColor="dark"
      buttonType="buy"
      buttonLocale={ currentLocale }
      buttonSizeMode="fill"
      paymentRequest={ paymentRequest }
      onLoadPaymentData={ paymentRequest => {
        setGooglePayOutgoingPayment({
          card: {
            card_name: paymentRequest.paymentMethodData.info.billingAddress.name,
            google_payments: paymentRequest.paymentMethodData.tokenizationData.token,
          },
          payment_type: 'google',
          payment_type_code: paymentRequest.paymentMethodData.info.cardNetwork.toLowerCase(),
        });

      }}
    />
  );
}

type BuildGoogleDataArgs = {|
  +merchantPublicKey: string,
  +merchantName: string,
  +amountUsd: string,
|};

function buildGooglePayPaymentRequest({ merchantPublicKey, merchantName, amountUsd }: BuildGoogleDataArgs): {} {
  return {
    apiVersion: 2,
    apiVersionMinor: 0,
    allowedPaymentMethods: [
      {
        type: 'CARD',
        parameters: {
          allowedAuthMethods: ['PAN_ONLY', 'CRYPTOGRAM_3DS'],
          allowedCardNetworks: ['MASTERCARD', 'VISA'],
          billingAddressRequired: true,
        },
        tokenizationSpecification: {
          type: 'PAYMENT_GATEWAY',
          parameters: {
            gateway: 'ebanx',
            gatewayMerchantId: merchantPublicKey,
          },
        },
      },
    ],
    merchantInfo: {
      merchantId: ENV_CONSTANTS.PWG_MERCHANT_ID,
      merchantName: merchantName,
    },
    transactionInfo: {
      totalPriceStatus: 'FINAL',
      totalPrice: amountUsd,
      currencyCode: 'USD',
      countryCode: 'US',
    },
  };
}
